import * as THREE from 'three'

// ---------------------------------------------------------------------------
// Recolha de triângulos em coordenadas de MUNDO a partir de um modelo
// Fragments.
//
// Porquê um colector próprio: no Fragments 3.4.3 o grafo de cena
// (`model.object`) vem VAZIO (zero meshes), o motor desenha por um
// sistema próprio. A geometria só é acessível por `getItemsGeometry`,
// que devolve, por item, uma lista de PARTES com:
//   - positions: Float32Array em coordenadas LOCAIS
//   - indices:   Uint16Array | Uint32Array
//   - normals:   Int16Array quantizadas (/32767), demasiado grosseiras
//                para cortar, por isso não são recolhidas aqui
//   - transform: matriz local -> modelo
// Mundo = matrixWorld(model.object) · transform(parte).
//
// A composição está verificada contra o `getBoxes` (que já devolve
// caixas de mundo) e é a mesma que o export KMZ usa em produção
// (`kmzGeometry.collectKmzMeshesFromModel`).
//
// Robustez: partes podem vir ilegíveis (streaming, buffers libertados).
// Cada parte vai em try/catch: salta-se, conta-se em `skipped` e nunca
// se rebenta.
//
// PRECISÃO (2026-08, quarta leva das Vistas 2D): num modelo
// georreferenciado (coordenadas na casa dos 1,37e6 m) guardar posições
// de MUNDO num Float32Array custa ~0,125 m de passo de quantização. Um
// pilar redondo de 20 cm de raio saía dentado e deslocado até ~6 cm. A
// composição local -> mundo já corre toda em float64 (o three usa
// números JS); o que se perdia era só o ARMAZENAMENTO final.
//
// Fix: `opts.origin`. Com ele, as posições devolvidas são RELATIVAS a
// essa origem (subtracção feita em float64, ANTES de quantizar), o que
// devolve números pequenos e um passo de Float32 na casa do micrómetro.
// Sem ele o comportamento é o de antes (mundo absoluto), para não
// partir chamadores que não saibam de origens.
// ---------------------------------------------------------------------------

/** Parte de geometria devolvida por `getItemsGeometry`. */
interface FragGeometryPart {
  positions: Float32Array
  indices?: Uint16Array | Uint32Array
  transform?: { elements: number[] }
}

/** Subconjunto do FragmentsModel de que precisamos. Tipado à parte para
 *  não acoplar ao tipo completo do motor (e para os testes poderem usar
 *  um modelo falso). */
export interface WorldTrianglesSource {
  object: THREE.Object3D
  getItemsIdsWithGeometry: () => Promise<number[]>
  getItemsGeometry: (ids: number[]) => Promise<unknown[]>
}

/** Triângulos de um elemento, já no mundo. As partes do item vêm
 *  fundidas num só buffer (índices reindexados), o que permite soldar
 *  arestas entre partes na detecção de arestas de feição. */
export interface WorldTriangleItem {
  localId: number
  /** xyz intercalado, metros, RELATIVO à `origin` do resultado (que é
   *  (0,0,0) quando o chamador não pede origem: nesse caso são
   *  coordenadas de mundo absolutas). */
  positions: Float32Array
  /** Sempre presente depois da fusão (as partes sem índices ganham uma
   *  sequência 0..n-1). */
  indices: Uint32Array
}

export interface WorldTrianglesResult {
  items: WorldTriangleItem[]
  triangles: number
  truncated: boolean
  skipped: number
  /** Origem a que as `positions` estão referidas. Mundo = posição +
   *  origin. */
  origin: [number, number, number]
}

/** Tecto por omissão: acima disto o corte 2D deixa de ser interactivo.
 *  Uma vista costuma consumir muito menos porque o chamador pré-filtra
 *  os ids pelas caixas de mundo. */
export const WORLD_TRIANGLES_MAX = 1_500_000

const _v = new THREE.Vector3()

/** Recolhe os triângulos dos elementos pedidos em coordenadas de mundo.
 *  `opts.ids` limita a recolha (pré-filtro por caixa do chamador); sem
 *  ids usa `getItemsIdsWithGeometry()`.
 *
 *  `opts.origin` (recomendado em modelos georreferenciados): devolve as
 *  posições RELATIVAS a esse ponto. A subtracção é feita em float64,
 *  antes de escrever no Float32Array, por isso o erro de quantização
 *  passa a ser proporcional ao tamanho do EDIFÍCIO e não à distância à
 *  origem do sistema de coordenadas. */
export async function collectWorldTriangles(
  model: WorldTrianglesSource,
  opts?: {
    ids?: number[]
    maxTriangles?: number
    chunkSize?: number
    origin?: readonly [number, number, number] | readonly number[]
  },
): Promise<WorldTrianglesResult> {
  const maxTri = opts?.maxTriangles ?? WORLD_TRIANGLES_MAX
  const chunk = opts?.chunkSize ?? 500
  const items: WorldTriangleItem[] = []
  let triangles = 0
  let truncated = false
  let skipped = 0
  const o = opts?.origin
  const ox = Number.isFinite(o?.[0]) ? Number(o?.[0]) : 0
  const oy = Number.isFinite(o?.[1]) ? Number(o?.[1]) : 0
  const oz = Number.isFinite(o?.[2]) ? Number(o?.[2]) : 0
  const origin: [number, number, number] = [ox, oy, oz]

  let ids: number[]
  if (opts?.ids) {
    ids = opts.ids
  } else {
    try {
      ids = await model.getItemsIdsWithGeometry()
    } catch {
      return { items, triangles, truncated, skipped, origin }
    }
  }
  if (!ids || ids.length === 0) {
    return { items, triangles, truncated, skipped, origin }
  }

  model.object.updateWorldMatrix(true, false)
  const objMatrix = model.object.matrixWorld
  const world = new THREE.Matrix4()
  const partMatrix = new THREE.Matrix4()

  for (let start = 0; start < ids.length; start += chunk) {
    if (truncated) break
    const slice = ids.slice(start, start + chunk)
    let raw: unknown[]
    try {
      raw = await model.getItemsGeometry(slice)
    } catch {
      skipped += slice.length
      continue
    }
    raw.forEach((item, i) => {
      if (truncated) return
      if (!item || typeof item !== 'object') return
      const localId = slice[i]
      const parts: unknown[] = Array.isArray(item)
        ? item
        : Object.values(item as Record<string, unknown>)

      // Buffers acumulados das partes deste item.
      const chunksPos: Float32Array[] = []
      const chunksIdx: Uint32Array[] = []
      let vertexBase = 0

      for (const rawPart of parts) {
        if (truncated) break
        const part = rawPart as FragGeometryPart | null
        const positions = part?.positions
        if (!positions || positions.length < 9) continue
        try {
          const vertexCount = Math.floor(positions.length / 3)
          const idx = part.indices
          const triCount = (idx ? idx.length : vertexCount) / 3
          if (!Number.isFinite(triCount) || triCount < 1) continue
          if (triangles + triCount > maxTri) {
            truncated = true
            break
          }
          world.copy(objMatrix)
          if (part.transform?.elements) {
            partMatrix.fromArray(part.transform.elements)
            world.multiply(partMatrix)
          }
          const out = new Float32Array(positions.length)
          for (let v = 0; v < vertexCount; v++) {
            // `applyMatrix4` corre todo em float64 (Vector3 usa números
            // JS). Subtrair a origem AQUI, antes de escrever no
            // Float32Array, é o que salva a precisão: quantiza-se um
            // número pequeno em vez de um na casa do milhão.
            _v.set(
              positions[v * 3],
              positions[v * 3 + 1],
              positions[v * 3 + 2],
            ).applyMatrix4(world)
            out[v * 3] = _v.x - ox
            out[v * 3 + 1] = _v.y - oy
            out[v * 3 + 2] = _v.z - oz
          }
          const outIdx = new Uint32Array(idx ? idx.length : vertexCount)
          if (idx) {
            for (let k = 0; k < idx.length; k++) outIdx[k] = idx[k] + vertexBase
          } else {
            for (let k = 0; k < vertexCount; k++) outIdx[k] = k + vertexBase
          }
          chunksPos.push(out)
          chunksIdx.push(outIdx)
          vertexBase += vertexCount
          triangles += triCount
        } catch {
          // Parte com buffers inconsistentes: salta-a, não rebenta.
          skipped++
        }
      }

      if (chunksPos.length === 0) return
      items.push({
        localId,
        positions: concatF32(chunksPos),
        indices: concatU32(chunksIdx),
      })
    })
  }

  return { items, triangles, truncated, skipped, origin }
}

function concatF32(chunks: Float32Array[]): Float32Array {
  if (chunks.length === 1) return chunks[0]
  let total = 0
  for (const c of chunks) total += c.length
  const out = new Float32Array(total)
  let o = 0
  for (const c of chunks) {
    out.set(c, o)
    o += c.length
  }
  return out
}

function concatU32(chunks: Uint32Array[]): Uint32Array {
  if (chunks.length === 1) return chunks[0]
  let total = 0
  for (const c of chunks) total += c.length
  const out = new Uint32Array(total)
  let o = 0
  for (const c of chunks) {
    out.set(c, o)
    o += c.length
  }
  return out
}
